import React, { useState, useEffect } from 'react';
import { Box, Grid, MenuItem, Select, Tooltip, Typography, Button, TextField, CircularProgress, Switch, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createClient, getClientCategories, getClientGroup, getClientSubCategories } from 'api/services/clients/clients';
import { getUsersActiveAndInactive, getFynGstResponse } from 'api/services/users';
import DrawerWrapper from 'components/DrawerWrapper';
import Loader from 'components/Loader';
import LoadingButton from 'components/LoadingButton';
import { snack } from 'components/toast';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { DialogProps, ResType } from 'types';
import { createClientDefaultValues, CreateClientSchema } from 'validations/createCllient';
import { isValidMobile } from 'validations/phoneNumbr';
import { ctbToCategoryMap, getAddressFromGst } from 'utils/gstCategoryMap';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useConfirm } from 'context/ConfirmDialog';
import ValidateAccess from 'components/ValidateAccess';
import { Permissions } from 'data/permissons';
import { getClientFormData, getClientPrefixId } from 'api/services/preferencess';
import countries from 'data/countries';
import Flag from 'react-world-flags';
import AddClientGroup from './addClientGroup';
import { Add } from '@mui/icons-material';
import { handleError } from 'utils/handleError';
import FormAutoCompleteWithSelectAll from 'components/FormFields/FormAutoCompleteWithSelectAll';
import youtubeLogo from "./../../../assets/images/youtube-logo.png";


interface Props extends DialogProps {
  successCb?: () => void;
}

interface Country {
  code: any;
  name: string;
  dialCode: string;
};

const roundedFieldSx = {
  bgcolor: '#fff',
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
};

// GST "Constitution of Business" (ctb) values, as returned by the GST portal.
const businessTypeOptions = [
  { label: 'Proprietorship', value: 'Proprietorship' },
  { label: 'Partnership', value: 'Partnership' },
  { label: 'Hindu Undivided Family', value: 'Hindu Undivided Family' },
  { label: 'Private Limited Company', value: 'Private Limited Company' },
  { label: 'Public Limited Company', value: 'Public Limited Company' },
  { label: 'Society/Club/Trust', value: 'Society/Club/Trust' },
  { label: 'Association of Persons', value: 'Association of Persons' },
  { label: 'Government Department', value: 'Government Department' },
  { label: 'Public Sector Undertaking', value: 'Public Sector Undertaking' },
  { label: 'Unlimited Company', value: 'Unlimited Company' },
  { label: 'Limited Liability Partnership', value: 'Limited Liability Partnership' },
  { label: 'Local Authority', value: 'Local Authority' },
  { label: 'Statutory Body', value: 'Statutory Body' },
  { label: 'Foreign Limited Liability Partnership', value: 'Foreign Limited Liability Partnership' },
  { label: 'Foreign Company Registered (in India)', value: 'Foreign Company Registered (in India)' },
  { label: 'One Person Company', value: 'One Person Company' },
  { label: 'Section 8 Company', value: 'Section 8 Company' },
  { label: 'Others (Please specify)', value: 'Others (Please specify)' },
];

// The client profile's Address tab (CommunicationAddressDetails / BillingAddressDetails / CopyAddressDetails)
// reads `data.address.communicationfulladdress` / `data.address.billingfulladdress` / `data.address.shippingfulladdress`
// / `data.address.billingState` / `data.issameaddress` — NOT a flat `officeAddress` field. Build that shape from
// the single Office Address field on this form; kept in sync onto the real `address`/`issameaddress` form fields
// by the effect below (watching officeAddress/state) so getValues() and the submit payload both carry it.
const buildAddressPayload = (formData: any) => ({
  address: {
    communicationfulladdress: formData?.officeAddress || '',
    billingfulladdress: formData?.officeAddress || '',
    shippingfulladdress: formData?.officeAddress || '',
    billingState: formData?.state || '',
  },
  issameaddress: true,
});

function SectionBadge({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'inline-block',
        bgcolor: '#14151A',
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.8125rem',
        px: 2,
        py: 0.75,
        borderRadius: '8px',
      }}
    >
      {children}
    </Box>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Box mb={0.25} display="flex" alignItems="baseline" gap={0.5}>
      <Typography variant="subtitle2" fontWeight={600} component="span">
        {children}
      </Typography>
      {required ? (
        <Typography variant="subtitle2" color="error.main" fontWeight={700} component="span">*</Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" component="span">(optional)</Typography>
      )}
    </Box>
  );
}

interface LabeledInputProps {
  control: any;
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  type?: string;
  inputProps?: any;
  InputProps?: any;
  disabled?: boolean;
}

function LabeledInput({ control, name, label, placeholder, required, multiline, type = 'text', inputProps, InputProps, disabled }: LabeledInputProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          <FieldLabel required={required}>{label}</FieldLabel>
          <TextField
            {...field}
            value={field.value ?? ''}
            fullWidth
            size="small"
            variant="outlined"
            placeholder={placeholder}
            multiline={multiline}
            rows={multiline ? 2 : 1}
            type={type}
            disabled={disabled}
            error={Boolean(error)}
            inputProps={inputProps}
            InputProps={InputProps}
            sx={roundedFieldSx}
          />
          {error && (
            <Typography variant="caption" sx={{ pl: '2px', display: 'block', mt: 0.5 }} color="rgb(211, 47, 47)">
              {error.message}
            </Typography>
          )}
        </Box>
      )}
    />
  );
}

interface LabeledSelectProps {
  control: any;
  name: string;
  label: string;
  placeholder: string;
  required?: boolean;
  options: Array<{ label: string; value: any }>;
  onValueChange?: (value: any) => void;
}

function LabeledSelect({ control, name, label, placeholder, required, options, onValueChange }: LabeledSelectProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          <FieldLabel required={required}>{label}</FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            error={Boolean(error)}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value);
              if (onValueChange) onValueChange(e.target.value);
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected: any) => {
                const match = options.find((o) => o.value === selected);
                if (!match) {
                  return <Typography color="text.disabled" component="span">{placeholder}</Typography>;
                }
                return match.label;
              },
              MenuProps: { PaperProps: { sx: { maxHeight: 300 } } },
            }}
            sx={roundedFieldSx}
          >
            {options.map((item, index) => (
              <MenuItem key={index} value={item.value}>{item.label}</MenuItem>
            ))}
          </TextField>
          {error && (
            <Typography variant="caption" sx={{ pl: '2px', display: 'block', mt: 0.5 }} color="rgb(211, 47, 47)">
              {error.message}
            </Typography>
          )}
        </Box>
      )}
    />
  );
}


const AddClient: React.FC<Props> = ({ open, setOpen, successCb }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [openAddGroup, setOpenAddGroup] = useState(false);
  const [groupList, setGroupList] = useState([]);
  const [addClientMethod, setAddClientMethod] = useState<'gstin' | 'manual'>('gstin');
  const [gstinText, setGstinText] = useState('');
  const [pendingSubCategoryName, setPendingSubCategoryName] = useState<string | null>(null);
  const [nonMandatoryExpanded, setNonMandatoryExpanded] = useState(false);

  const addClientMethods: { key: 'gstin' | 'manual'; label: string; icon: React.ReactNode }[] = [
    { key: 'gstin', label: 'Enter GSTIN', icon: <DescriptionOutlinedIcon /> },
    { key: 'manual', label: 'Enter Manually', icon: <EditOutlinedIcon /> },
  ];

  const gstinPattern = /^[0-9]{2}[A-Z]{3}[A,B,C,F,G,H,L,J,P,T]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}[1-9A-Za-z]{1}[Z]{1}[0-9A-Z]{1}$/;

  const {
    mutate: fetchGstDetailsMutate,
    isLoading: fetchingGstDetails,
  } = useMutation<any, any, string>((gstNumber: string) => getFynGstResponse({ gstNumber }), {
    onSuccess: (response: any, gstNumber: string) => {
      if (response?.data?.sts?.toUpperCase() !== 'ACTIVE') {
        const status = response?.data?.data?.sts;
        console.log(response)
        if(response?.data?.error?.message){
          snack.error(response?.data?.error?.message);
        } else if (!response?.data?.status) {
          snack.error("We're currently unable to verify GST information. Please try again or enter details manually.");
        } else {
          snack.error(`${status} GST! Please enter a valid GSTIN`);
        }
        return;
      }

      const result = response;
      setValue('gstNumber', gstNumber, { shouldValidate: true });
      setValue('legalName', result?.data?.lgnm || '', { shouldValidate: true });
      setValue('tradeName', result?.data?.tradeNam || '', { shouldValidate: true });
      setValue('displayName', result?.data?.tradeNam || result?.data?.lgnm || '', { shouldValidate: true });
      setValue('placeOfSupply', result?.data?.pradr?.addr?.stcd || '', { shouldValidate: true });
      setValue('officeAddress', getAddressFromGst(result?.data?.pradr?.addr), { shouldValidate: true });
      setValue('buildingName', result?.data?.pradr?.addr?.bnm || '', { shouldValidate: true });
      setValue('street', result?.data?.pradr?.addr?.st || '', { shouldValidate: true });
      setValue('city', result?.data?.pradr?.addr?.dst || '', { shouldValidate: true });
      setValue('state', result?.data?.pradr?.addr?.stcd || '', { shouldValidate: true });
      setValue('pincode', result?.data?.pradr?.addr?.pncd || '', { shouldValidate: true });
      setValue('gstRegistrationDate', result?.data?.rgdt || '', { shouldValidate: true });
      setValue('registrationType', result?.data?.dty || '', { shouldValidate: true });
      setValue('panNumber', gstNumber.substring(2, gstNumber.length - 3), { shouldValidate: true });
      setValue('gstRegistered', 'yes', { shouldValidate: true });
      setValue('gstVerified', true, { shouldValidate: true });

      const ctb = result?.data?.ctb || '';
      setValue('constitutionOfBusiness', ctb, { shouldValidate: true });
      const ctbMapping = ctbToCategoryMap[ctb.trim().toLowerCase()];
      if (ctbMapping) {
        const matchedCategory = categories.find((c: any) => c.name === ctbMapping.category);
        if (matchedCategory) {
          // Step 1: select the category. This flips `selectedCategoryId`, which triggers
          // the getClientSubCategories query below to fetch that category's sub-categories.
          setValue('category', matchedCategory.id, { shouldValidate: true });
          // Step 2: remember which sub-category name to pick once that fetch resolves
          // (handled by the effect watching `subCategories` further down).
          setPendingSubCategoryName(ctbMapping.subCategory || null);
        }
      }
      snack.success('GST details fetched');
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 422) {
        snack.error('Invalid GSTIN');
      } else if (!err?.error) {
        snack.error(err?.response?.data?.message || 'An error occurred while fetching GST details');
      }
    },
  });

  const handleFetchGstDetails = () => {
    if (!gstinText) {
      snack.error('Enter GST Number');
      return;
    }
    if (gstinText.length !== 15 || !gstinPattern.test(gstinText)) {
      snack.error('Invalid GSTIN Pattern');
      return;
    }
    fetchGstDetailsMutate(gstinText);
  };
  const { data: categoriesData }: ResType =
    useQuery(
      'client-categories',
      getClientCategories,
      {
        enabled: open,
      },
    );

  const categories =
    categoriesData?.data || [];


  const { data: clientGroup, isLoading: clientGroupLoading }: ResType = useQuery("client-group", getClientGroup, {
    onSuccess: (res) => {
      setGroupList(res?.data?.result);
    },
    onError: (err) => {
      if (!err?.error) {
        snack.error(`An error occurred while fetching user-data`);
      }
    },
    enabled: Boolean(nonMandatoryExpanded)
  });

  const { data: users, isLoading: userLoading }: ResType = useQuery("users", getUsersActiveAndInactive, {
    enabled: open,
    onError: (err) => {
      if (!err?.error) {
        snack.error(`An error occurred while fetching user-data`);
      }
    }
  });

  const { data: clientPrefixId, isLoading: clientPrefixIdLoading }: ResType = useQuery("client-Prefix-Id", getClientPrefixId, {
    enabled: open,
  });

  const { data: clientFormData, isLoading: clientFormDataLoading }: ResType = useQuery("client-form-data", getClientFormData, {
    enabled: open,
    onSuccess: (res) => {
      const formData = res?.data;
      setValue("countryCode", formData?.defaultCountryCode || "IN");
      setValue("dialCode", formData?.defaultDialCode || "91");
      setValue("mobileNumber", formData?.defaultMobileNumber || "");
      setValue("email", formData?.defaultEmailId || "");
      setValue("clientManagers", formData?.clientManagers?.map((item: any) => ({ id: item.id, fullName: item.fullName, label: item.fullName, value: item.id })) || []);
    }
  });

  const { mutate, isLoading } = useMutation(createClient, {
    onSuccess: (res: any) => {
      snack.success("Client Created");
      setOpen(false);
      if (successCb) {
        successCb();
        return;
      }
      reset(createClientDefaultValues);
      navigate({
        pathname: `/clients/${res.data.id}/profile/`,
        search: `?displayName=${res.data?.displayName}&clientId=${res.data?.clientId}`,
      });
    },
    onError: (err: any) => {
      if (err && err?.response && err?.response?.data && err?.response?.data?.message) {
        if (err.response.data.message === "Client Number Exists") {
          confirm({
            msg: "Assigning unique client number improves recordkeeping. However, for related clients, using the same number can be helpful for tracking purposes.  Would you like to create a unique number for this client, or proceed with the current number?",
            action: () => {
              const allFormData: any = getValues();
              mutate({
                ...allFormData,

                // MAP OLD FIELDS
                newCategoryId:
                  allFormData.category,

                newSubCategoryId:
                  allFormData.subCategory,

                clientManagers:
                  allFormData.clientManagers?.map(
                    item => item.id,
                  ),

                gstVerified:
                  allFormData?.gstRegistered ===
                  "yes" &&
                  allFormData?.gstVerified,

                panVerified:
                  allFormData?.gstRegistered ===
                  "no" &&
                  allFormData?.panVerified,

                clientPortalAccess:
                  allFormData?.clientPortalAccess ===
                  "yes",

                clientNumberDuplicate: false,
              });

            },
          });
        } else {
          snack.error(handleError(err));
        }
      } else if (!err.error) {
        snack.error('An error occurred while creating Client')
      }
    },
  });



  const { watch, control, handleSubmit, reset, getValues, setValue } = useForm({
    defaultValues: createClientDefaultValues,
    mode: "onChange",
    resolver: yupResolver(CreateClientSchema(),),
  });

  const setData = (data: any) => {
    reset({
      ...getValues(),
      ...data,
    });
  };


  const onFormSubmit = (data: any) => {

    if (!data.displayName.trim().length) {
      snack.error("Invalid Display Name");
      return;
    }

    if (!isValidMobile(data.mobileNumber, data.countryCode)) {
      snack.error("Please enter a valid Mobile Number");
      return;
    }

    if (
      subCategories?.length > 0 &&
      !data?.subCategory
    ) {
      snack.error(
        "Please select Sub Category",
      );
      return;
    }

    mutate({
      ...data,

      // OLD FIELDS -> NULL
      category: null,
      subCategory: null,

      // NEW DB FIELDS
      newCategoryId: data.category,

      newSubCategoryId:
        data.subCategory,

      clientManagers:
        data.clientManagers.map(
          item => item.id,
        ),

      gstVerified:
        data?.gstRegistered === "yes" &&
        data?.gstVerified,

      panVerified:
        data?.gstRegistered === "no" &&
        data?.panVerified,

      clientPortalAccess:
        data?.clientPortalAccess === "yes",

      clientNumberDuplicate: true,

      clientGroup:
        watch('clientGroup'),
    });
  };

  useEffect(() => {
    if (clientPrefixId) {
      setValue("clientNumber", clientPrefixId?.data?.prefix);
    }
  }, [clientPrefixId, setValue]);

  useEffect(() => {
    setValue("mobileNumber", '')
  }, [watch("countryCode"), setValue]);

  // Keeps the `address`/`issameaddress` fields (read by the client profile's Address tab —
  // see buildAddressPayload above) in sync with the single Office Address field and state,
  // so they're real form values and show up in getValues()/the submit payload without
  // needing to be assembled separately at submit time.
  useEffect(() => {
    setValue('address', buildAddressPayload(getValues()).address, { shouldValidate: true });
    setValue('issameaddress', true, { shouldValidate: true });
  }, [watch('officeAddress'), watch('state'), setValue, getValues]);

  const selectedCategoryId =
    watch('category');

  const {
    data: subCategoriesData,
  } = useQuery(
    [
      'client-sub-categories',
      selectedCategoryId,
    ],
    () =>
      getClientSubCategories(
        selectedCategoryId as unknown as number,
      ),
    {
      enabled: !!selectedCategoryId,
    },
  );

  const subCategories =
    subCategoriesData?.data || [];

  useEffect(() => {
    setValue(
      'subCategory',
      null,
    );
  }, [watch('category')]);

  // Runs once the sub-categories for the GST-selected category have actually loaded,
  // then picks the one matching ctbToCategoryMap for the fetched GSTIN (see handleFetchGstDetails above).
  useEffect(() => {
    if (!pendingSubCategoryName) return;
    if (!subCategories?.length) return;
    const matchedSubCategory = (subCategories as any[]).find((s: any) => s.name === pendingSubCategoryName);
    if (matchedSubCategory) {
      setValue('subCategory', matchedSubCategory.id, { shouldValidate: true });
    }
    setPendingSubCategoryName(null);
  }, [subCategories, pendingSubCategoryName, setValue]);



  return (
    <DrawerWrapper
      open={open}
      setOpen={() => {
        reset(createClientDefaultValues);
        setOpen(false);
      }}
      title="Add a New Client"
    >
      {userLoading || clientGroupLoading ? (
        <Loader />
      ) : (
        <form onSubmit={handleSubmit(onFormSubmit)} style={{ marginTop: "-10px" }}>
          <Grid item xs={12} height="100vh" overflow="auto">
            <Typography variant="body2" color="text.secondary">
              Fetch by GSTIN, or fill it in yourself.
            </Typography>
            <Box display="flex" flexWrap="wrap" alignItems="stretch" gap={3.5} mt={0.75}>
              {addClientMethods.map((method) => {
                const selected = addClientMethod === method.key;
                return (
                  <Box
                    key={method.key}
                    onClick={() => setAddClientMethod(method.key)}
                    sx={{
                      flex: '0 1 auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      border: '1px solid',
                      borderColor: selected ? 'secondary.main' : 'divider',
                      bgcolor: selected ? (theme) => alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.50,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s, background-color 0.2s',
                      '&:hover': {
                        borderColor: 'secondary.main',
                      },
                    }}
                  >
                    <Box sx={{ color: selected ? 'secondary.main' : 'text.secondary', display: 'flex' }}>
                      {method.icon}
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color={selected ? 'secondary.main' : 'text.primary'}
                    >
                      {method.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {addClientMethod === 'gstin' && (
              <Box mt={0.75}>
                <Typography variant="subtitle2" fontWeight={600} mb={0.75}>
                  Enter GSTIN number
                </Typography>
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    sx={roundedFieldSx}
                    placeholder="29AACCV1726H1ZA"
                    value={gstinText}
                    onChange={(e) => setGstinText(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !fetchingGstDetails) {
                        e.preventDefault();
                        handleFetchGstDetails();
                      }
                    }}
                    inputProps={{ maxLength: 15 }}
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                    disabled={fetchingGstDetails}
                    onClick={handleFetchGstDetails}
                  >
                    {fetchingGstDetails ? <CircularProgress size={18} color="inherit" /> : 'Fetch'}
                  </Button>
                </Box>
              </Box>
            )}

            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <SectionBadge>Mandatory Fields *</SectionBadge>

              {/* YouTube help button */}
              <Button sx={{ ml: 'auto', minWidth: 0 }}>
                <a
                  href="https://youtu.be/CVvW4dsm4-U"
                  target="_blank"
                  rel="Adding Client in ATOM"
                >
                  <img
                    src={youtubeLogo}
                    alt="YouTube logo"
                    style={{
                      width: '28px',
                      height: '24px',
                      cursor: 'pointer',
                    }}
                  />
                </a>
              </Button>
            </Box>

            <Box mt={0.75}>
              <LabeledSelect
                control={control}
                required
                name="category"
                label="Client category"
                placeholder="Select category"
                options={categories.map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Box>
            {subCategories?.length > 0 && (
              <Box mt={0.5}>
                <LabeledSelect
                  control={control}
                  required
                  name="subCategory"
                  label="Sub category"
                  placeholder="Select sub category"
                  options={subCategories.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                  }))}
                />
              </Box>
            )}
            <Box mt={0.5}>
              <LabeledInput
                control={control}
                name="displayName"
                label="Client name"
                required
                placeholder="e.g. Croma — Infiniti Retail Ltd"
                inputProps={{ maxLength: 100 }}
              />
            </Box>
            <Box display='flex' mt={0.5}>
              <Box width='70%' mr={1}>
                <FieldLabel required>Country</FieldLabel>
                <Controller
                  name="countryCode"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      size="small"
                      sx={roundedFieldSx}
                      onChange={(e) => {
                        const selectedCountry = countries.find((c) => c.code === e.target.value);
                        field.onChange(e);
                        if (selectedCountry) {
                          setValue('dialCode', selectedCountry?.phone);
                        }
                      }}
                    >
                      {countries.map((country, index) => (
                        <MenuItem key={index} value={country.code}>
                          <Flag code={country.code} style={{ width: 24, marginRight: 8 }} />
                          {country.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
              <Box width='30%'>
                <LabeledInput
                  control={control}
                  name="dialCode"
                  label="Dial code"
                  required
                  disabled
                />
              </Box>
            </Box>
            <Grid container spacing={1} mt={0.01}>
              <Grid item xs={6}>
                <LabeledInput
                  control={control}
                  name="mobileNumber"
                  label="Mobile number"
                  type='number'
                  required
                  placeholder="98765 43210"
                  inputProps={{
                    maxLength: 15,
                  }}
                  InputProps={
                    <Tooltip title="Please enter the Mobile Number you use for WhatsApp">
                      <Typography sx={{ display: "flex" }}>
                        <span style={{ color: "#B2BEB5" }}>|</span>&nbsp;
                        <WhatsAppIcon style={{ color: "#25D366" }} />
                      </Typography>
                    </Tooltip>
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledInput
                  control={control}
                  name="email"
                  label="Email ID"
                  required
                  placeholder="contact@company.com"
                />
              </Grid>
            </Grid>
            <Box mt={1}>
              <FormAutoCompleteWithSelectAll
                control={control}
                label="Client Manager *"
                multiple
                name="clientManagers"
                options={users?.data.map((item: any) => ({
                  label: item.fullName,
                  value: item.id,
                  id: item.id,
                  fullName: item.fullName
                }))}
              />
            </Box>

            <Accordion
              expanded={nonMandatoryExpanded}
              onChange={(_e, isExpanded) => setNonMandatoryExpanded(isExpanded)}
              disableGutters
              elevation={0}
              square={false}
              sx={{
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '10px !important',
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 2,
                  '& .MuiAccordionSummary-content': { alignItems: 'center', my: 1 },
                }}
              >
                <SectionBadge>Non-Mandatory Fields</SectionBadge>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
                {/* <Grid container spacing={1} mt={0.01}>
                </Grid> */}
                <Box mt={1}>
                  <LabeledInput
                    control={control}
                    name="officeAddress"
                    label="Office address"
                    placeholder="Building, street, locality, city, state, PIN"
                    multiline
                  />
                </Box>
                <Grid container spacing={1} mt={0.01}>
                  <Grid item xs={6}>
                    <LabeledSelect
                      control={control}
                      name="clientGroup"
                      label="Client group"
                      placeholder="Select group"
                      options={groupList.map((item: any) => ({
                        label: item.displayName,
                        value: item.id,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <LabeledInput
                      control={control}
                      name="clientNumber"
                      label="Client file #"
                      inputProps={{
                        maxLength: 31,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} mt={0.01}>
                  <Grid item xs={6}>
                    <LabeledInput
                      control={control}
                      name="authorizedPerson"
                      label="Authorized person"
                      placeholder="Name"
                      inputProps={{ maxLength: 50 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <LabeledInput
                      control={control}
                      name="designation"
                      label="Designation"
                      placeholder="e.g. Director"
                      inputProps={{ maxLength: 50 }}
                    />
                  </Grid>
                </Grid>
                <Box mt={1}>
                  <ValidateAccess name={Permissions.CLIENT_PORTAL_ACCESS}>
                    <Controller
                      name="clientPortalAccess"
                      control={control}
                      render={({ field }) => (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', px: 2, py: 1 }}
                        >
                          <Typography variant="subtitle2" fontWeight={600}>Client portal access</Typography>
                          <Switch
                            checked={field.value === 'yes'}
                            onChange={(e) => field.onChange(e.target.checked ? 'yes' : 'no')}
                          />
                        </Box>
                      )}
                    />
                  </ValidateAccess>
                </Box>
              </AccordionDetails>
            </Accordion>
            <LoadingButton
              loading={isLoading}
              disabled={isLoading}
              fullWidth
              sx={{ mt: 2 }}
              type="submit"
              loadingColor="white"
              title="Create Client"
              color="secondary"
            />
          </Grid>
        </form>
      )}
      <AddClientGroup open={openAddGroup} setOpen={setOpenAddGroup} watch={watch} setValue={setValue} />
    </DrawerWrapper>
  );
}

export default AddClient;